// SCR-08 tool connection (plan 8.4.8, 11.11): my connections, the 6-step wizard (level → tool → collect token →
// settings → test question → result), app connection (gateway tokens, key registry), team status, support table,
// "what is recorded" coverage. Tokens are shown once at issue time; the server keeps HMAC + prefix only.
import { z } from 'zod';
import {
  ConnectionIssueKey,
  ConnectionStatus,
  ConsentItem,
  MissingReason,
  Provider,
  RecordLevel,
  SetupOs,
  SupportItem,
  Availability,
  EvidenceLevel,
  ContentPolicy,
  CredentialPurpose,
  CredentialScope,
  Tool,
  TokenKind,
  VersionState,
  DropReason,
} from '../enums.js';
import { Count, EpochMs, Id, LocalizedText, PersonRef, TeamRef, UserId, Ymd } from '../common.js';

export const CLI_TOOLS = ['claude_code', 'codex'] as const;
export const CliTool = z.enum(CLI_TOOLS);
export type CliTool = z.infer<typeof CliTool>;

export const IdParams = z.object({ id: Id });

/** GET /api/v1/connect/config — server addresses and thresholds the screen must not hard-code. */
export const ConnectConfigResponse = z.object({
  serverUrl: z.url(),
  otlpLogsUrl: z.url(),
  otlpMetricsUrl: z.url(),
  tokenHeader: z.literal('x-aiax-hub-token'),
  gatewayUrls: z.object({ anthropic: z.url(), openai: z.url() }).nullable(),
  installers: z.object({ unix: z.url(), windows: z.url() }),
  helperCommand: z.string().nullable(),
  cliVersions: z.object({ claude_code: z.object({ min: z.string(), latest: z.string(), checking: z.array(z.string()) }), codex: z.object({ min: z.string(), latest: z.string(), checking: z.array(z.string()) }) }),
  thresholds: z.object({ quietAfterHours: z.int(), downAfterDays: z.int(), expiryNoticeDays: z.int(), testWaitMinutes: z.int(), gatewayDayOptions: z.array(z.int()) }),
  policy: z.object({ maxLevel: RecordLevel, defaultLevel: RecordLevel, caps: z.record(ConsentItem, z.boolean()), noticeVersion: z.string() }),
  gatewayEnabled: z.boolean(),
  retentionDays: z.object({ text: z.int().positive(), usage: z.int().positive() }),
  /** Empty lists mean no model is permitted, never an unrestricted wildcard. */
  allowedModels: z.object({ anthropic: z.array(z.string()), openai: z.array(z.string()) }),
});
export type ConnectConfigResponse = z.infer<typeof ConnectConfigResponse>;

export const ConnectionIssue = z.object({
  key: ConnectionIssueKey,
  days: z.int().nonnegative().optional(),
  count: z.int().nonnegative().optional(),
  /** sends_more_than_consent: the consent item the CLI still sends and the setting line to remove. */
  item: ConsentItem.optional(),
  settingLine: z.string().optional(),
  /** legacy_settings: the old PoC address detected on that PC. */
  address: z.string().optional(),
});
export type ConnectionIssue = z.infer<typeof ConnectionIssue>;

export const TokenSummary = z.object({
  id: Id,
  kind: TokenKind,
  prefix: z.string(),
  status: z.enum(['active', 'expired', 'revoked']),
  createdAt: EpochMs,
  expiresAt: EpochMs.nullable(),
  lastUsedAt: EpochMs.nullable(),
});
export type TokenSummary = z.infer<typeof TokenSummary>;

/** One tool on one PC (plan 8.4.8 내 연결 card). */
export const ConnectionView = z.object({
  id: Id,
  deviceId: Id,
  deviceName: z.string(),
  /** Device OS as reported ("macOS 15", "Windows 11"); null before the first record. */
  deviceOs: z.string().max(40).nullable(),
  tool: CliTool,
  mode: z.enum(['direct', 'helper']),
  status: ConnectionStatus,
  /** Error disconnect (helper not responding, token expired…) as opposed to plain silence. */
  problem: z.boolean(),
  issues: z.array(ConnectionIssue),
  lastSeenAt: EpochMs.nullable(),
  cliVersion: z.string().nullable(),
  versionState: z.union([VersionState, z.literal('unknown')]),
  /** The person's consent level (record level is per person, not per connection). */
  recordLevel: RecordLevel.nullable(),
  token: TokenSummary.nullable(),
  revokedAt: EpochMs.nullable(),
});
export type ConnectionView = z.infer<typeof ConnectionView>;

export const DeviceView = z.object({ id: Id, name: z.string(), os: z.string().nullable(), helper: z.boolean(), firstConnectedAt: EpochMs.nullable(), lastSeenAt: EpochMs.nullable() });
export type DeviceView = z.infer<typeof DeviceView>;

export const COLLECTION_DAY_STATES = ['received', 'all_dropped', 'no_receipt', 'before_connection'] as const;
export const CollectionDay = z.object({
  date: Ymd, state: z.enum(COLLECTION_DAY_STATES), received: Count, stored: Count, duplicates: Count, dropped: Count,
  dropReasons: z.partialRecord(DropReason, Count),
});
export type CollectionDay = z.infer<typeof CollectionDay>;
/** Latest 14 organisation calendar dates, including today; independent of screen period filters. */
export const CollectionCalendar = z.object({ timezone: z.string(), from: Ymd, to: Ymd, days: z.array(CollectionDay).length(14) });
export type CollectionCalendar = z.infer<typeof CollectionCalendar>;

/** GET /api/v1/connect/devices — my PCs and their tool connections (revoked ones included). */
export const MyConnectionsResponse = z.object({
  devices: z.array(DeviceView.extend({ connections: z.array(ConnectionView), collection: CollectionCalendar.optional() })),
  summary: z.object({ active: Count, attention: Count, revoked: Count }),
});
export type MyConnectionsResponse = z.infer<typeof MyConnectionsResponse>;

/** PATCH /api/v1/connect/devices/:id — rename (no real names / host names; plan 11.4.5). */
export const DeviceRenameRequest = z.object({ name: z.string().trim().min(1).max(30) });

export const DEVICE_EVENTS = ['token_issued', 'first_record', 'version_changed', 'last_record', 'went_quiet', 'went_down', 'error', 'revoked', 'level_changed'] as const;
export const DeviceHistoryResponse = z.object({
  events: z.array(z.object({ at: EpochMs, kind: z.enum(DEVICE_EVENTS), tool: CliTool.nullable(), version: z.string().nullable(), level: RecordLevel.nullable(), issue: ConnectionIssueKey.nullable() })).max(50),
});

/** POST /api/v1/connect/connections/:id/revoke — own, or someone else's with devices.revoke_any (owner notified). */
export const RevokeConnectionResponse = z.object({ revokedTokens: Count });

/** POST /api/v1/connect/tests — start a "test record" wait for a connection or a just-issued token. */
export const TestStartRequest = z.union([z.object({ connectionId: Id }).strict(), z.object({ tokenId: Id }).strict()]);
export const TestStartResponse = z.object({ testId: Id, since: EpochMs, timeoutSec: z.int().positive() });
/** GET /api/v1/connect/tests/:id — poll. stored ✓ / received but not stored (reasons) / not received in time. */
export const TestStatusResponse = z.object({
  phase: z.enum(['waiting', 'checking', 'done']),
  outcome: z.enum(['stored', 'dropped', 'not_received']).nullable(),
  reasons: z.array(MissingReason),
  receivedAt: EpochMs.nullable(),
  cliVersion: z.string().nullable(),
  deviceName: z.string().nullable(),
  elapsedSec: z.int().nonnegative(),
});

/** POST /api/v1/connect/collect-tokens — wizard ③: one token per PC × tool; re-issuing revokes the previous one. */
export const CollectTokenRequest = z
  .object({
    tool: CliTool,
    deviceId: Id.optional(),
    newDeviceName: z.string().trim().min(1).max(30).optional(),
    os: SetupOs.optional(),
    /** "설정 다시 받기" on an existing connection. */
    connectionId: Id.optional(),
  })
  .superRefine((b, ctx) => {
    if (!b.deviceId && !b.newDeviceName && !b.connectionId) ctx.addIssue({ code: 'custom', message: 'deviceId, newDeviceName or connectionId is required', path: ['deviceId'] });
  });
export const CollectTokenResponse = z.object({
  /** Shown once; never retrievable again. */
  token: z.string().startsWith('axh_ing_'),
  tokenId: Id,
  prefix: z.string(),
  deviceId: Id,
  connectionId: Id.nullable(),
  expiresAt: EpochMs,
  replacedTokenId: Id.nullable(),
});

/** GET /api/v1/connect/setup-snippet — wizard ④ (rendered by packages/cli-config so it matches what ingest expects). */
export const SetupSnippetQuery = z.object({
  tool: CliTool,
  os: SetupOs,
  mode: z.enum(['command', 'paste']),
  level: z.coerce.number().int().min(1).max(4).optional(),
  /** Custom item list instead of a level (comma separated consent items). */
  items: z.string().regex(/^[a-z_]+(,[a-z_]+)*$/).optional(),
  /** "설정 다시 받기" with an unchanged token: only the level lines are rendered. */
  keepToken: z.enum(['0', '1']).default('0'),
});
export const SNIPPET_NOTES = ['codex_answers_unofficial', 'codex_work_detail_filtered', 'codex_keep_other_otel', 'metrics_untouched', 'tool_output_off', 'backup_made'] as const;
/**
 * One line of the paste-mode settings box (mockup connect.js codeLines): `base` = always written, `on` = turned on by
 * the chosen level (highlighted), `off` = written as off on purpose, `note` = a comment line the web writes in the
 * viewer's language from `note` (TOML `# …`; JSON has none).
 */
export const SnippetLine = z.object({ kind: z.enum(['base', 'on', 'off', 'note']), text: z.string().max(400).nullable(), note: z.enum(SNIPPET_NOTES).nullable() });
export const SetupSnippetResponse = z.object({
  mode: z.enum(['command', 'paste']),
  /** One-line installer command (the token is asked when it runs, never embedded). */
  command: z.string().nullable(),
  revertCommand: z.string(),
  /** Paste mode: user-level settings file only (never a project folder, plan 11.11.2). */
  targetFile: z.string().nullable(),
  format: z.enum(['json', 'toml', 'shell']),
  /** Paste mode lines; the token value appears as `tokenPlaceholder` inside a `base` line. */
  lines: z.array(SnippetLine).nullable(),
  /** Placeholder the web replaces with the token it still holds in memory (paste mode). */
  tokenPlaceholder: z.literal('{{AIAX_TOKEN}}'),
  notes: z.array(z.enum(SNIPPET_NOTES)),
});

/** Gateway tokens (P4, plan 11.11.1): company-key access for an app, ≤ 90 days, model allow-list. */
export const GatewayTokenRow = z.object({
  id: Id,
  prefix: z.string(),
  appName: z.string(),
  purpose: z.string(),
  owner: PersonRef,
  provider: Provider,
  /** 'meta' = basic info only (default); 'text' = also the last question/answer (owner, purpose, redaction required). */
  textPolicy: ContentPolicy,
  models: z.array(z.string()).nullable(),
  status: z.enum(['active', 'expired', 'revoked']),
  createdAt: EpochMs,
  expiresAt: EpochMs,
  lastUsedAt: EpochMs.nullable(),
});
export const GatewayTokenListResponse = z.object({ items: z.array(GatewayTokenRow) });
export const GatewayTokenRequest = z.object({
  /** Used in helper commands → slug only. */
  appName: z.string().regex(/^[a-z0-9][a-z0-9-]{1,39}$/),
  purpose: z.string().trim().min(1).max(80),
  ownerId: UserId.optional(),
  provider: Provider,
  textPolicy: ContentPolicy.default('meta'),
  expiresInDays: z.union([z.literal(30), z.literal(60), z.literal(90)]),
  models: z.array(z.string().max(80)).max(20).optional(),
});
export const GatewayTokenIssuedResponse = z.object({ token: z.string().startsWith('axh_gw_'), row: GatewayTokenRow });

/** Key registry (plan 6.2.2 api_keys): alias · last 4 · provider key id · workspace · owner · source · state. */
export const ApiKeyRow = z.object({
  id: Id,
  provider: Provider,
  alias: z.string(),
  last4: z.string().length(4),
  providerKeyId: z.string().nullable(),
  workspace: z.string().nullable(),
  owner: PersonRef.nullable(),
  team: TeamRef.nullable(),
  source: z.enum(['admin_api', 'manual']),
  status: z.enum(['active', 'grace', 'revoked']),
  graceUntil: EpochMs.nullable(),
  viaGateway: z.boolean(),
  inOrgReport: z.boolean(),
  lastUsedAt: EpochMs.nullable(),
});
/** GET /api/v1/connect/api-keys — own keys; keys.view → all keys. */
export const ApiKeyListResponse = z.object({ items: z.array(ApiKeyRow), canRotate: z.boolean() });
/** POST /api/v1/admin/api-keys — manual registry entry (key value never stored; last 4 + provider key id only). */
export const ApiKeyCreateRequest = z.object({
  provider: Provider,
  alias: z.string().trim().min(1).max(40),
  last4: z.string().length(4),
  providerKeyId: z.string().max(120).nullable().default(null),
  workspace: z.string().max(120).nullable().default(null),
  ownerId: UserId.nullable().default(null),
  teamId: Id.nullable().default(null),
  viaGateway: z.boolean().default(false),
  inOrgReport: z.boolean().default(true),
});
/** PATCH /api/v1/admin/api-keys/:id — owner/team/alias mapping (links org-report key ids to people). */
export const ApiKeyUpdateRequest = z.object({ alias: z.string().trim().min(1).max(40).optional(), ownerId: UserId.nullable().optional(), teamId: Id.nullable().optional() });
/** POST /api/v1/admin/api-keys/:id/rotate (integrations.manage): active → 7-day grace → revoked. */
export const ApiKeyRotateRequest = z.object({ newKey: z.string().min(8).max(400) });
export const ApiKeyRotateResponse = z.object({ previous: ApiKeyRow, current: ApiKeyRow });

/** Company provider credentials (gateway company key, org Admin key) — envelope-encrypted, never shown again. */
export const ProviderCredentialRow = z.object({
  id: Id,
  provider: Provider,
  purpose: CredentialPurpose,
  alias: z.string(),
  last4: z.string().length(4),
  scope: CredentialScope,
  team: TeamRef.nullable(),
  user: PersonRef.nullable(),
  status: z.enum(['active', 'grace', 'revoked']),
  createdAt: EpochMs,
  rotatedAt: EpochMs.nullable(),
});
export const ProviderCredentialListResponse = z.object({ items: z.array(ProviderCredentialRow) });
export const ProviderCredentialCreateRequest = z.object({
  provider: Provider,
  purpose: CredentialPurpose,
  alias: z.string().trim().min(1).max(40),
  secret: z.string().min(8).max(400),
  scope: CredentialScope.default('org'),
  teamId: Id.optional(),
  userId: UserId.optional(),
});
export const ProviderCredentialRotateRequest = z.object({ secret: z.string().min(8).max(400) });

/** GET /api/v1/connect/team-status — person × tool table, 'needs attention' first (manager team / admin all). */
export const TeamStatusQuery = z.object({ scope: z.enum(['team', 'all']), team: Id.optional(), filter: z.enum(['attention', 'all']).default('attention') });
export const TEAM_STATUS_RANKS = ['problem', 'down', 'never', 'quiet', 'ok', 'revoked'] as const;
export const TeamStatusResponse = z.object({
  rows: z.array(
    z.object({
      person: PersonRef,
      team: TeamRef.nullable(),
      tool: CliTool.nullable(),
      deviceName: z.string().nullable(),
      rank: z.enum(TEAM_STATUS_RANKS),
      status: ConnectionStatus.nullable(),
      issues: z.array(ConnectionIssue),
      lastSeenAt: EpochMs.nullable(),
      cliVersion: z.string().nullable(),
      versionState: z.union([VersionState, z.literal('unknown')]).nullable(),
      connectionId: Id.nullable(),
      canRevoke: z.boolean(),
    }),
  ),
  summary: z.object({ people: Count, attentionPeople: Count, errorRows: Count }),
  /** Row counts of both filter choices ("확인할 것 n · 모두 m"), whatever `filter` was asked. */
  counts: z.object({ attention: Count, all: Count }),
  expiringTokens: z.array(z.object({ person: PersonRef, kind: TokenKind, prefix: z.string(), target: z.string(), expiresAt: EpochMs.nullable(), status: z.enum(['active', 'expired']) })),
  /** Manager scope: members outside the scope (other managers, admins, content reviewers). */
  excludedPeople: Count,
});

/** GET /api/v1/connect/support — tool × item support table with evidence (plan 5.9). Cells anchor `support-<tool>-<item>`. */
export const SupportCell = z.object({ evidence: EvidenceLevel.nullable(), status: Availability, note: LocalizedText.nullable(), checkedAt: EpochMs.nullable().optional() });
export type SupportCell = z.infer<typeof SupportCell>;
export const SupportMatrixResponse = z.object({
  tools: z.array(z.object({ id: z.string().regex(/^[a-f0-9]{16}$/).optional(), tool: Tool, version: LocalizedText, os: z.string(), auth: LocalizedText, cells: z.record(SupportItem, SupportCell) })),
  cliVersions: z.record(CliTool, z.object({ min: z.string(), latest: z.string(), checking: z.array(z.string()) })),
  checkedAt: EpochMs,
  mine: z.array(z.object({ tool: CliTool, deviceName: z.string(), version: z.string().nullable(), versionState: z.union([VersionState, z.literal('unknown')]), lastSeenAt: EpochMs.nullable() })),
});

/** GET /api/v1/connect/coverage — "무엇이 기록되나요" (plan 8.4.8): items × channels + attribute detail table. */
export const COVERAGE_ITEMS = ['prompt', 'usage', 'files', 'changes', 'time', 'model', 'other'] as const;
export const COVERAGE_CHANNELS = ['claude_code', 'codex', 'anthropic_api', 'openai_api', 'org_report'] as const;
export const CoverageResponse = z.object({
  matrix: z.array(
    z.object({
      item: z.enum(COVERAGE_ITEMS),
      cells: z.record(z.enum(COVERAGE_CHANNELS), z.object({ value: z.enum(['yes', 'conditional', 'no', 'na']), level: RecordLevel.nullable(), minVersion: z.string().nullable(), condition: z.string().nullable(), note: LocalizedText.optional() })),
    }),
  ),
  assessedAt: EpochMs,
  attributes: z.array(z.object({ tool: CliTool, name: z.string(), stored: z.boolean(), where: z.string().nullable(), level: RecordLevel.nullable() })),
});

export const connectSchemas = {
  IdParams,
  ConnectConfigResponse,
  MyConnectionsResponse,
  DeviceRenameRequest,
  DeviceView,
  DeviceHistoryResponse,
  RevokeConnectionResponse,
  TestStartRequest,
  TestStartResponse,
  TestStatusResponse,
  CollectTokenRequest,
  CollectTokenResponse,
  SetupSnippetQuery,
  SetupSnippetResponse,
  GatewayTokenListResponse,
  GatewayTokenRequest,
  GatewayTokenIssuedResponse,
  ApiKeyListResponse,
  ApiKeyRow,
  ApiKeyCreateRequest,
  ApiKeyUpdateRequest,
  ApiKeyRotateRequest,
  ApiKeyRotateResponse,
  ProviderCredentialListResponse,
  ProviderCredentialCreateRequest,
  ProviderCredentialRotateRequest,
  ProviderCredentialRow,
  TeamStatusQuery,
  TeamStatusResponse,
  SupportMatrixResponse,
  CoverageResponse,
} as const;
